// frontend/src/settings/PasskeySettings.js
// パスキー (WebAuthn) の登録・管理画面。
// - 登録: Edge Function `passkey-register-begin` → navigator.credentials.create → `passkey-register-finish`
// - 一覧: user_passkeys テーブルから自分のパスキーを取得 (RLS で本人のみ閲覧可)
// - 削除: 行削除 (RLS で本人のみ削除可)

import React, { useEffect, useState, useCallback } from "react";
import { Key, Trash2, Plus, Shield, AlertTriangle } from "lucide-react";
import { supabase } from "../supabase";
import { startRegistration } from "@simplewebauthn/browser";
import Header from "../components/ui/Header";

const SUPABASE_URL = "https://tpxzotpgkcaioifgzlii.supabase.co";

const callAuthed = async (path, body) => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("セッション切れ");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : "{}",
  });
  const json = await res.json();
  if (!res.ok || json.success === false) {
    throw new Error(json?.message || `${path} failed`);
  }
  return json;
};

export default function PasskeySettings() {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [registering, setRegistering] = useState(false);
  const [label, setLabel] = useState("");

  const fetchPasskeys = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: e } = await supabase
        .from("user_passkeys")
        .select("id, credential_id, label, device_type, backed_up, created_at, last_used_at")
        .order("created_at", { ascending: false });
      if (e) throw e;
      setPasskeys(data || []);
    } catch (e) {
      setError(`取得エラー: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys]);

  const handleRegister = async () => {
    setError(""); setSuccess("");
    if (typeof window === "undefined" || !window.PublicKeyCredential) {
      setError("このブラウザはパスキーに対応していません");
      return;
    }
    setRegistering(true);
    try {
      // 1. チャレンジ取得
      const begin = await callAuthed("passkey-register-begin", { label });
      console.log("🔑 passkey-register-begin response:", begin);
      if (!begin?.options?.challenge || !begin?.options?.user?.id) {
        throw new Error(`サーバーが不正な options を返しました: ${JSON.stringify(begin?.options)}`);
      }
      // 2. ブラウザのパスキー作成 (Touch ID / Face ID / セキュリティキー)
      //    v10.0.0 は options を直接渡す API (後の v11+ は {optionsJSON: ...})。
      const attestation = await startRegistration(begin.options);
      // 3. サーバーで検証 & 保存
      await callAuthed("passkey-register-finish", {
        challengeId: begin.challengeId,
        attestationResponse: attestation,
        label,
      });
      setSuccess("✓ パスキーを登録しました");
      setLabel("");
      await fetchPasskeys();
    } catch (e) {
      if (e.name === "NotAllowedError" || /cancell?ed|aborted/i.test(e.message || "")) {
        setError("キャンセルされました");
      } else {
        setError(`登録失敗: ${e.message || e}`);
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このパスキーを削除しますか?\n(端末側の情報は残る場合があります)")) return;
    setError(""); setSuccess("");
    try {
      const { error: e } = await supabase.from("user_passkeys").delete().eq("id", id);
      if (e) throw e;
      setSuccess("✓ パスキーを削除しました");
      await fetchPasskeys();
    } catch (e) {
      setError(`削除失敗: ${e.message}`);
    }
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>
          <Shield size={20} style={{ color: "#1d4ed8", marginRight: 8, verticalAlign: "middle" }} />
          パスキー (生体認証) 設定
        </h1>
        <p style={styles.subtitle}>
          Touch ID / Face ID / 指紋 / セキュリティキーで、パスワードなしで安全にログインできます。
          複数端末 (iPhone, iPad, PC) それぞれで登録すると便利です。
        </p>

        {error && <div style={styles.errorBox}><AlertTriangle size={14} /> {error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>新しいパスキーを登録</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ラベル (例: iPhone, 職場 PC)"
              maxLength={60}
              style={styles.input}
            />
            <button
              onClick={handleRegister}
              disabled={registering}
              style={{
                ...styles.primaryBtn,
                opacity: registering ? 0.5 : 1,
                cursor: registering ? "not-allowed" : "pointer",
              }}
            >
              <Plus size={14} />
              {registering ? "登録中..." : "パスキーを登録"}
            </button>
          </div>
          <p style={styles.hint}>
            ボタン押下後、ブラウザから生体認証 or セキュリティキーの確認が出ます。
          </p>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>登録済みパスキー ({passkeys.length})</h2>
          {loading ? (
            <div style={{ padding: 20, color: "#64748b" }}>読み込み中...</div>
          ) : passkeys.length === 0 ? (
            <div style={{ padding: 20, color: "#94a3b8", fontSize: 13 }}>
              まだパスキーが登録されていません。 上のフォームから登録してください。
            </div>
          ) : (
            <div style={styles.list}>
              {passkeys.map((pk) => (
                <div key={pk.id} style={styles.row}>
                  <Key size={16} style={{ color: "#1d4ed8", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                      {pk.label || "(無名)"}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      登録: {new Date(pk.created_at).toLocaleString("ja-JP")}
                      {pk.last_used_at && (
                        <> / 最終使用: {new Date(pk.last_used_at).toLocaleString("ja-JP")}</>
                      )}
                      {pk.device_type && (
                        <> / {pk.device_type === "multiDevice" ? "同期パスキー" : "単体端末"}</>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(pk.id)}
                    style={styles.dangerBtn}
                    title="削除"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.infoBox}>
          <strong>補足:</strong> パスキーは端末やパスワードマネージャ (iCloud Keychain, 1Password 等) に
          保存され、QR コードや同期で他端末でも使えます。 ScrubEdge のサーバーにはパスワードは一切保存されません。
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", background: "#f8fafc" },
  content: { maxWidth: 800, margin: "0 auto", padding: 24 },
  title: { fontSize: 22, fontWeight: 700, color: "#0f172a", margin: "0 0 8px" },
  subtitle: { fontSize: 13, color: "#64748b", lineHeight: 1.6, marginBottom: 20 },
  card: {
    background: "white",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
  },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", margin: "0 0 12px" },
  input: {
    flex: 1, minWidth: 200, padding: "8px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 14,
  },
  primaryBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "9px 18px",
    background: "linear-gradient(135deg, #1d4ed8, #1e40af)",
    color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700,
    cursor: "pointer",
  },
  dangerBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 32, height: 32,
    background: "#fef2f2", color: "#b91c1c",
    border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer",
  },
  hint: { fontSize: 11, color: "#94a3b8", marginTop: 8 },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  row: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px",
    background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0",
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12,
    display: "flex", alignItems: "center", gap: 6,
  },
  successBox: {
    background: "#f0fdf4", border: "1px solid #86efac", color: "#166534",
    padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12,
  },
  infoBox: {
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af",
    padding: 12, borderRadius: 8, fontSize: 12, lineHeight: 1.6,
  },
};
